import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { EntraIdAuthRepository } from '../repositories/entra-id-auth.repository';
import { EntraIdService } from './entra-id.service';

@Injectable({ providedIn: 'root' })
export class EntraIdAuthService {
  public loggingIn$ = new BehaviorSubject<boolean>(true);
  private _loginProgress = new BehaviorSubject<boolean>(false);
  private _loggedIn = new BehaviorSubject<boolean>(false);
  private _loginAttempt = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly entraIdService: EntraIdService,
    private readonly entraIdAuthRepository: EntraIdAuthRepository,
  ) {
    this.entraIdService.loginProgress$.subscribe((value) => {
      this.loggingIn$.next(value);
      this._loginProgress.next(value);
    });

    this.entraIdService.isLoggedIn().subscribe((isLoggedIn) => {
      this._loggedIn.next(isLoggedIn);
    });

    this.entraIdService.loginSuccess().subscribe((loginResult) => {
      this._loginAttempt.next(loginResult);
      if (loginResult) {
        this._loggedIn.next(true);
      }
      setTimeout(() => {
        this._loginAttempt.next(false);
      }, 3000);
    });
  }

  // Log the user in and redirect them if MSAL provides a redirect URI otherwise go to the default URI
  login(): void {
    this.entraIdService.login();
  }

  // Log the user out
  logout(): void {
    this.entraIdService.logout();
  }

  /**
   * Logs the user out of the platform silently, which basically prevents the selection of a user to log out of the platform.
   * It can be used to prevent the application to redirect on logout.
   */
  logoutSilent(): void {
    this.entraIdService.silentLogout();
  }

  /**
   * Tracks if the user is authorized by entraId and always remembers the last login status.
   */
  isLoggedIn(): Observable<boolean> {
    return this.entraIdService.isLoggedIn();
  }

  /**
   * Tracks if a login against entraId was successful or not.
   *
   * This event is emitted based on the loginSuccess event by the underlying entra-id.service
   * After a successful event it switches back to false after a few seconds.
   * Based on this event an application can execute its own login logic e.g. fetching user data.
   */
  loginAttempt(): Observable<boolean> {
    return this._loginAttempt.asObservable();
  }

  /**
   * Tracks the login progress of entraId login
   */
  loginProgress(): Observable<boolean> {
    return this._loginProgress.asObservable();
  }

  /**
   * Validates the current token by checking the expiration date.
   */
  validateToken(): boolean {
    const token = this.entraIdService.getIdToken();
    const expires_at = this.entraIdService.getExpiration();
    if (token && expires_at) {
      return this.checkExpiration(expires_at);
    } else {
      return false;
    }
  }

  /**
   * Validates the current token by sending a request to the backend.
   *
   * @returns boolean
   *  */
  async validateTokenAsync(): Promise<boolean> {
    const token = this.entraIdService.getIdToken();
    if (!token) {
      return false;
    } else {
      return this.entraIdAuthRepository.checkToken(token);
    }
  }

  private checkExpiration(expiration: string): boolean {
    return moment().isBefore(Number(expiration));
  }
}
